import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, IconButton, Tooltip, ToggleButton, ToggleButtonGroup, Divider,
} from '@mui/material';
import {
  RadioButtonUnchecked,
  Undo,
  Delete,
  Brush,
  AutoFixNormal,
  Remove,
  CropSquare,
  Title as TextIcon,
  FiberManualRecord,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Crop as CropIcon,
  DragIndicator,
} from '@mui/icons-material';
import AreaCaptureOverlay from './AreaCaptureOverlay';

const TOOL = { CIRCLE: 'circle', PENCIL: 'pencil', ERASER: 'eraser', LINE: 'line', RECT: 'rect', TEXT: 'text' };
const STROKE_WIDTHS = { S: 2, M: 4, L: 8 };
const ERASER_WIDTHS = { S: 12, M: 24, L: 40 };

/**
 * QuestionAnnotationCanvas
 *
 * Wraps its children with a transparent HTML Canvas overlay.
 * Students can annotate the question with the full drawing tool set.
 * Screen captures (crop icon) are drawn directly onto the annotation canvas.
 *
 * Props:
 *   questionIdx  — current question index (resets drawings when question changes)
 *   visible      — whether the canvas overlay is active
 *   onCapture    — optional; if provided, screen captures are sent here (to the drawing pad)
 *                  instead of being drawn onto the annotation canvas
 */
export default function QuestionAnnotationCanvas({ children, questionIdx, visible, onCapture }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const snapshotRef = useRef(null);     // ImageData before current stroke (for shape preview)
  const undoSnapshotRef = useRef(null); // data-URL before current stroke (for undo history)
  const strokeStartRef = useRef(null);  // { startX, startY }

  const [activeTool, setActiveTool] = useState(TOOL.PENCIL);
  const [drawColor, setDrawColor] = useState('#000000');
  const [strokeSize, setStrokeSize] = useState('M');
  const [history, setHistory] = useState([]);
  const [canUndo, setCanUndo] = useState(false);
  const [toolbarCollapsed, setToolbarCollapsed] = useState(false);
  const [textCursor, setTextCursor] = useState(null); // { x, y, text }
  const [fontSize, setFontSize] = useState(20);
  const [captureMode, setCaptureMode] = useState(false);
  const [pos, setPos] = useState({ x: 16, y: 72 });
  const isDragging = useRef(false);
  const dragOffset = useRef({ dx: 0, dy: 0 });

  // ---- Drag handlers for floating toolbar ----
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    dragOffset.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    const onMove = (me) => {
      if (!isDragging.current) return;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 60, me.clientX - dragOffset.current.dx)),
        y: Math.max(0, Math.min(window.innerHeight - 40, me.clientY - dragOffset.current.dy)),
      });
    };
    const onUp = () => {
      isDragging.current = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [pos.x, pos.y]);

  // ---- Reset canvas when question changes ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHistory([]);
    setCanUndo(false);
    setTextCursor(null);
    setPos({ x: 16, y: 72 });
  }, [questionIdx]);

  // ---- Size canvas backing store to match the container ----
  // The container grows after first paint (MathJax typeset, image loads, diagram
  // snapshots). If the backing store is only sized once, CSS height:100% stretches
  // those fixed pixels across the taller element and strokes land in the wrong
  // place — looking like the drawing "drifts" while scrolling. Re-run on every
  // container resize so backing-store size always equals the displayed size.
  const resizeCanvasToContainer = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const newW = Math.round(rect.width * dpr);
    const newH = Math.round(rect.height * dpr);

    if (newW === 0 || newH === 0 || (canvas.width === newW && canvas.height === newH)) return;

    // Preserve any existing annotation strokes across resize
    const tmp = document.createElement('canvas');
    tmp.width = canvas.width;
    tmp.height = canvas.height;
    if (canvas.width > 0 && canvas.height > 0) tmp.getContext('2d').drawImage(canvas, 0, 0);

    const prevCssW = canvas.width / dpr;
    const prevCssH = canvas.height / dpr;

    canvas.width = newW;
    canvas.height = newH;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    if (tmp.width > 0 && tmp.height > 0) {
      ctx.drawImage(tmp, 0, 0, prevCssW, prevCssH);
    }
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    resizeCanvasToContainer();
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => resizeCanvasToContainer());
    ro.observe(container);
    return () => ro.disconnect();
  }, [visible, resizeCanvasToContainer]);

  // ---- Helpers ----
  const relPos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const saveSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    return canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
  }, []);

  const saveUndoSnapshot = useCallback(() => canvasRef.current.toDataURL('image/png'), []);

  // ---- Pointer handlers ----
  const handlePointerDown = useCallback((e) => {
    if (!visible) return;
    e.preventDefault();

    if (activeTool === TOOL.TEXT) {
      const { x, y } = relPos(e);
      setTextCursor({ x, y, text: '' });
      return;
    }

    canvasRef.current.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const { x, y } = relPos(e);

    snapshotRef.current = saveSnapshot();
    undoSnapshotRef.current = saveUndoSnapshot();
    strokeStartRef.current = { startX: x, startY: y };

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (activeTool === TOOL.PENCIL) {
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  }, [visible, activeTool, relPos, saveSnapshot, saveUndoSnapshot]);

  const handlePointerMove = useCallback((e) => {
    if (!drawingRef.current || !strokeStartRef.current) return;
    e.preventDefault();
    const { x, y } = relPos(e);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { startX, startY } = strokeStartRef.current;
    const width = activeTool === TOOL.ERASER ? ERASER_WIDTHS[strokeSize] : STROKE_WIDTHS[strokeSize];

    if (activeTool === TOOL.PENCIL) {
      ctx.strokeStyle = drawColor;
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineTo(x, y);
      ctx.stroke();
    } else if (activeTool === TOOL.ERASER) {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(x, y, width / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else {
      // Shape tools: restore snapshot then draw live preview
      ctx.putImageData(snapshotRef.current, 0, 0);
      ctx.strokeStyle = drawColor;
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();

      if (activeTool === TOOL.LINE) {
        ctx.moveTo(startX, startY);
        ctx.lineTo(x, y);
        ctx.stroke();
      } else if (activeTool === TOOL.RECT) {
        ctx.strokeRect(startX, startY, x - startX, y - startY);
      } else if (activeTool === TOOL.CIRCLE) {
        const cx = (startX + x) / 2;
        const cy = (startY + y) / 2;
        const rx = Math.abs(x - startX) / 2;
        const ry = Math.abs(y - startY) / 2;
        ctx.save();
        ctx.fillStyle = drawColor + '30';
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
    }
  }, [activeTool, drawColor, strokeSize, relPos]);

  const handlePointerUp = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;

    if (activeTool === TOOL.PENCIL) {
      canvasRef.current.getContext('2d').closePath();
    }

    if (undoSnapshotRef.current) {
      setHistory((prev) => [...prev, undoSnapshotRef.current]);
      setCanUndo(true);
      undoSnapshotRef.current = null;
    }
    snapshotRef.current = null;
    strokeStartRef.current = null;
  }, [activeTool]);

  // ---- Undo ----
  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    const newHistory = history.slice(0, -1);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const img = new Image();
    img.onload = () => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    img.src = prev;
    setHistory(newHistory);
    setCanUndo(newHistory.length > 0);
  }, [history]);

  // ---- Clear ----
  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    setHistory((prev) => [...prev, canvas.toDataURL('image/png')]);
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    setCanUndo(true);
  }, []);

  // ---- Captured screenshot → send to drawing pad if available, else draw onto annotation canvas ----
  const handleCapturedImage = useCallback((dataUrl) => {
    if (onCapture) {
      onCapture(dataUrl);
      const canvas = canvasRef.current;
      if (canvas) {
        setHistory((prev) => [...prev, canvas.toDataURL('image/png')]);
        setCanUndo(true);
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      setHistory((prev) => [...prev, canvas.toDataURL('image/png')]);
      setCanUndo(true);
      const cssW = canvas.width / dpr;
      const cssH = canvas.height / dpr;
      const ratio = Math.min(cssW / img.width, cssH / img.height, 1);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.drawImage(img, 0, 0, w, h);
    };
    img.src = dataUrl;
  }, [onCapture]);

  // ---- Commit text cursor to canvas ----
  const commitTextCursor = useCallback(() => {
    if (!textCursor || !textCursor.text.trim()) { setTextCursor(null); return; }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');
    setHistory((prev) => [...prev, canvas.toDataURL('image/png')]);
    setCanUndo(true);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.font = `${fontSize}px sans-serif`;
    ctx.fillStyle = drawColor;
    const lineH = fontSize * 1.35;
    textCursor.text.split('\n').forEach((line, i) => {
      ctx.fillText(line, textCursor.x, textCursor.y + i * lineH);
    });
    setTextCursor(null);
  }, [textCursor, fontSize, drawColor]);

  // ---- Keyboard shortcut: Ctrl+Z ----
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); handleUndo(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleUndo]);

  return (
    <Box>
      {visible && (
        <>
          {toolbarCollapsed ? (
            /* Collapsed floating chip */
            <Box
              onPointerDown={handleDragStart}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.5,
                bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4,
                position: 'fixed', left: pos.x, top: pos.y,
                zIndex: 1300, boxShadow: 4,
                cursor: 'grab', touchAction: 'none',
                visibility: captureMode ? 'hidden' : 'visible',
              }}
            >
              <DragIndicator sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Tooltip title="Expand annotation tools">
                <IconButton size="small" onPointerDown={(e) => e.stopPropagation()} onClick={() => setToolbarCollapsed(false)} sx={{ p: 0.25 }}>
                  <KeyboardArrowDown fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box sx={{ fontSize: '0.68rem', color: 'text.secondary', fontStyle: 'italic', userSelect: 'none' }}>
                Annotate
              </Box>
            </Box>
          ) : (
            /* Expanded floating window */
            <Box
              sx={{
                position: 'fixed', left: pos.x, top: pos.y,
                zIndex: 1300, boxShadow: 6,
                bgcolor: 'background.paper',
                border: '1px solid', borderColor: 'divider',
                borderRadius: 2,
                maxWidth: 'calc(100vw - 32px)',
                visibility: captureMode ? 'hidden' : 'visible',
                overflow: 'hidden',
              }}
            >
              {/* Window title bar — draggable */}
              <Box
                onPointerDown={handleDragStart}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1, py: 0.5,
                  bgcolor: '#fff8e1', borderBottom: '1px solid #ffe082',
                  cursor: 'grab', touchAction: 'none', userSelect: 'none',
                }}
              >
                <DragIndicator sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
                <Box sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', flexGrow: 1 }}>
                  Annotation Tools
                </Box>
                <Box sx={{ fontSize: '0.65rem', color: 'text.disabled', fontStyle: 'italic', mr: 0.5 }}>
                  {onCapture
                    ? 'marks here not submitted — Capture sends area to your answer'
                    : 'reference only — not submitted'}
                </Box>
                <Tooltip title="Minimise toolbar">
                  <IconButton size="small" onPointerDown={(e) => e.stopPropagation()} onClick={() => setToolbarCollapsed(true)} sx={{ p: 0.25 }}>
                    <KeyboardArrowUp fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Tool buttons row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>

              {/* Drawing tools */}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={activeTool}
                onChange={(_, v) => v && setActiveTool(v)}
                sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.5 } }}
              >
                <Tooltip title="Freehand draw">
                  <ToggleButton value={TOOL.PENCIL}><Brush fontSize="small" /></ToggleButton>
                </Tooltip>
                <Tooltip title="Eraser">
                  <ToggleButton value={TOOL.ERASER}><AutoFixNormal fontSize="small" /></ToggleButton>
                </Tooltip>
                <Tooltip title="Straight line">
                  <ToggleButton value={TOOL.LINE}><Remove fontSize="small" /></ToggleButton>
                </Tooltip>
                <Tooltip title="Rectangle">
                  <ToggleButton value={TOOL.RECT}><CropSquare fontSize="small" /></ToggleButton>
                </Tooltip>
                <Tooltip title="Circle / Ellipse">
                  <ToggleButton value={TOOL.CIRCLE}><RadioButtonUnchecked fontSize="small" /></ToggleButton>
                </Tooltip>
                <Tooltip title="Type text on diagram">
                  <ToggleButton value={TOOL.TEXT}><TextIcon fontSize="small" /></ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

              {/* Stroke width */}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={strokeSize}
                onChange={(_, v) => v && setStrokeSize(v)}
                sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.5 } }}
              >
                <Tooltip title="Thin">
                  <ToggleButton value="S"><FiberManualRecord sx={{ fontSize: 8 }} /></ToggleButton>
                </Tooltip>
                <Tooltip title="Medium">
                  <ToggleButton value="M"><FiberManualRecord sx={{ fontSize: 13 }} /></ToggleButton>
                </Tooltip>
                <Tooltip title="Thick">
                  <ToggleButton value="L"><FiberManualRecord sx={{ fontSize: 18 }} /></ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

              {/* Color picker */}
              <Tooltip title="Draw colour">
                <Box
                  component="input"
                  type="color"
                  value={drawColor}
                  onChange={(e) => setDrawColor(e.target.value)}
                  sx={{ width: 32, height: 32, border: '1px solid #ccc', borderRadius: 1, cursor: 'pointer', p: 0, bgcolor: 'transparent', flexShrink: 0 }}
                />
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

              {/* Font size (only when text tool active) */}
              {activeTool === TOOL.TEXT && (
                <>
                  <Tooltip title="Font size">
                    <Box
                      component="select"
                      value={fontSize}
                      onChange={(e) => setFontSize(Number(e.target.value))}
                      sx={{ fontSize: '0.75rem', border: '1px solid #ccc', borderRadius: 1, px: 0.5, py: 0.25, cursor: 'pointer', bgcolor: 'background.paper' }}
                    >
                      {[12, 16, 20, 24, 28, 32, 40].map((s) => (
                        <option key={s} value={s}>{s}px</option>
                      ))}
                    </Box>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                </>
              )}

              {/* Undo / Clear */}
              <Tooltip title="Undo (Ctrl+Z)">
                <span>
                  <IconButton size="small" onClick={handleUndo} disabled={!canUndo}>
                    <Undo fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear all marks">
                <span>
                  <IconButton size="small" onClick={handleClear} disabled={!canUndo && history.length === 0}>
                    <Delete fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

              {/* Screenshot / area capture → sends to drawing pad if available */}
              <Tooltip title={onCapture ? 'Capture area → use as answer (drawing pad)' : 'Capture a screen area and insert it into the drawing pad'}>
                <IconButton size="small" onClick={() => setCaptureMode(true)}>
                  <CropIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            </Box>
          )}
        </>
      )}

      <Box ref={containerRef} sx={{ position: 'relative' }}>
        {children}

        {/* Text input overlay */}
        {visible && textCursor && (
          <Box
            component="textarea"
            autoFocus
            value={textCursor.text}
            onChange={(e) => setTextCursor((prev) => ({ ...prev, text: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitTextCursor(); }
              if (e.key === 'Escape') setTextCursor(null);
            }}
            onBlur={commitTextCursor}
            sx={{
              position: 'absolute',
              left: textCursor.x,
              top: textCursor.y - fontSize,
              minWidth: 120,
              background: 'transparent',
              border: '1px dashed #1976d2',
              outline: 'none',
              resize: 'both',
              fontFamily: 'sans-serif',
              fontSize: `${fontSize}px`,
              color: drawColor,
              lineHeight: 1.35,
              zIndex: 30,
              padding: '2px 4px',
              overflow: 'hidden',
            }}
            rows={1}
            placeholder="Type here…"
          />
        )}

        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: visible ? 'auto' : 'none',
            cursor: visible
              ? activeTool === TOOL.ERASER ? 'cell'
              : activeTool === TOOL.TEXT ? 'text'
              : 'crosshair'
              : 'default',
            zIndex: 10,
            touchAction: 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
      </Box>

      {captureMode && (
        <AreaCaptureOverlay
          onCapture={(dataUrl) => { handleCapturedImage(dataUrl); setCaptureMode(false); }}
          onCancel={() => setCaptureMode(false)}
        />
      )}
    </Box>
  );
}
